import { el } from './ui.js';
import { icon, type IconName } from './icons.js';

// Le quattro destinazioni restano uguali in ogni schermata. Le pagine secondarie
// appartengono sempre a una voce: nessuna schermata resta senza orientamento.
export const primaryNavigation = [
  { hash: '#home', icona: 'home', label: 'Casa', match: ['home', 'novita', 'posta', 'luoghi'] },
  { hash: '#scegli', icona: 'mic', label: 'Racconta', match: ['scegli', 'registra', 'spunti'] },
  { hash: '#ricordi', icona: 'archive', label: 'Ricordi', match: ['ricordi', 'tema', 'storia', 'cerca', 'cronologia', 'ricette', 'chiedi', 'leggi', 'album'] },
  { hash: '#menu', icona: 'menu', label: 'Menu', match: ['menu', 'famiglia', 'persona', 'modifica-persona', 'aggiungi-persona', 'persone-rimosse', 'albero', 'identita-famiglia', 'foto', 'voci', 'libro', 'ordine', 'impostazioni', 'aiuto'] },
] satisfies Array<{ hash: string; icona: IconName; label: string; match: string[] }>;

type Shortcut = { hash: string; icon: IconName; title: string; description: string; tint: number };
const shortcuts = {
  ricordi: { hash: '#ricordi', icon: 'archive', title: 'I tuoi ricordi', description: 'Le ultime storie e i capitoli', tint: 2 },
  foto: { hash: '#foto', icon: 'photos', title: 'Le fotografie', description: 'Sfoglia e aggiungi le tue foto', tint: 5 },
  // «La famiglia» apre l'albero: le persone, i volti e i legami stanno lì. #famiglia resta valido.
  famiglia: { hash: '#albero', icon: 'family', title: 'La famiglia', description: 'L’albero e le persone dei tuoi racconti', tint: 3 },
  libro: { hash: '#libro', icon: 'book', title: 'Il tuo libro', description: 'Le tue storie, da leggere insieme', tint: 11 },
} satisfies Record<string, Shortcut>;

function shortcut(item: Shortcut): HTMLElement {
  return el('a', { class: 'menu-voce', href: item.hash, style: `--tinta:var(--tinta-${item.tint})` },
    el('span', { class: 'icona' }, icon(item.icon)),
    el('span', { class: 'menu-nome' }, item.title),
    el('span', { class: 'sotto' }, item.description),
  );
}

export function homeShortcuts(): HTMLElement {
  return el('section', { class: 'home-sfoglia', 'aria-labelledby': 'sfoglia-title' },
    el('div', { class: 'sezione-testata' },
      el('h2', { id: 'sfoglia-title', class: 'sezione' }, 'Sfoglia il quaderno'),
      el('a', { class: 'link-quaderno', href: '#menu' }, 'Tutto il menu →'),
    ),
    el('div', { class: 'menu-griglia home-scorciatoie' }, ...Object.values(shortcuts).map(shortcut)),
  );
}

export function notebookMenu(): HTMLElement {
  const group = (id: string, title: string, items: Shortcut[]): HTMLElement =>
    el('section', { class: 'menu-sezione', 'aria-labelledby': id },
      el('h2', { class: 'sezione', id }, title),
      el('div', { class: 'menu-griglia' }, ...items.map(shortcut)),
    );
  return el('div', { class: 'menu-quaderno' },
    el('p', { class: 'data-oggi' }, 'Tutto al suo posto. Scegli che cosa ti va di fare.'),
    group('menu-storie', 'Ritrova una storia', [
      // «Cerca» è la stessa ricerca che c'è in cima a «I tuoi ricordi»: #cerca resta valido.
      { ...shortcuts.ricordi, description: 'Cerca, gli ultimi racconti e i capitoli' },
      shortcuts.libro,
      { hash: '#cronologia', icon: 'clock', title: 'La tua storia nel tempo', description: 'Racconti e foto, anno dopo anno', tint: 10 },
      { hash: '#ricette', icon: 'recipe', title: 'Il ricettario', description: 'Le ricette di casa, dettate da te', tint: 6 },
      { hash: '#chiedi', icon: 'chat', title: 'Parla col tuo quaderno', description: 'Fai una domanda o dimmi cosa correggere', tint: 7 },
    ]),
    group('menu-famiglia', 'Persone e fotografie', [
      shortcuts.famiglia, shortcuts.foto,
      { hash: '#voci', icon: 'mic', title: 'Le voci della famiglia', description: 'I racconti dei tuoi cari', tint: 9 },
    ]),
    group('menu-insieme', 'Continuiamo insieme', [
      { hash: '#spunti', icon: 'messages', title: 'Spunti di conversazione', description: 'Le domande da cui partire', tint: 1 },
      { hash: '#posta', icon: 'envelope', title: 'La tua posta', description: 'Le novità e tutto quello che vi siete detti', tint: 8 },
      { hash: '#ordine', icon: 'check', title: 'Mettiamo in ordine', description: 'Controlliamo nomi, date e parentele', tint: 3 },
    ]),
    el('section', { class: 'menu-sezione', 'aria-labelledby': 'menu-aiuto' },
      el('h2', { class: 'sezione', id: 'menu-aiuto' }, 'Per usare il quaderno'),
      el('div', { class: 'menu-servizio' },
        el('a', { class: 'link-quaderno', href: '#impostazioni' }, '⚙️ Impostazioni e grandezza del testo'),
        el('a', { class: 'link-quaderno', href: '#aiuto' }, '🛟 Come si usa'),
      ),
    ),
  );
}
